<template>
  <div class="bank-page">
    <div class="top-bar">
      <a class="back-link" href="#" @click.prevent="$emit('back')">← Back to Benevolism</a>
      <div class="top-brand"><span class="mark">B</span> THE BENEVOLISM SERIES — VOL. III</div>
    </div>

    <main>
      <!-- HERO -->
      <section class="hero">
        <v-container class="hero-inner" style="max-width:1180px;">
          <div class="cover-col intro-rise">
            <div class="cover-card">
              <div class="cover-crown">♛</div>
              <div class="cover-title">THE<br><strong>BENEVOLENT<br>BANK</strong></div>
              <div class="cover-sub">How Benevolism Would Transform Finance,<br>Democratize Wealth, and Free Millions From<br>the Machinery of Financial Extraction</div>
              <div class="cover-divider" />
              <div class="cover-scales">⚖</div>
              <div class="cover-divider" />
              <div class="cover-series">The Benevolism Series — Volume III</div>
              <div class="cover-author">ENRICO MICHAEL SEVERE</div>
              <div class="cover-heir">Davidic Heir, Son of King David</div>
            </div>
            <div class="badge">
              <div>
                <div class="badge-top">BENEVOLISM SERIES</div>
                <div class="badge-main">VOLUME</div>
                <div class="badge-bottom">III</div>
              </div>
            </div>
          </div>
          <div class="info-col intro-rise-delayed">
            <div class="eyebrow" style="text-align:left;">Volume III — The Benevolism Series</div>
            <h1 class="info-title">The Benevolent Bank</h1>
            <p class="info-sub">How Benevolism Would Transform Finance, Democratize Wealth, and Free Millions From the Machinery of Financial Extraction</p>
            <p class="info-desc">
              The third volume in the Benevolism Series. Five founding principles, ten binding laws, and a five-part Financial Pledge for transforming a financial system built on extraction into one built to serve the people who depend on it — because almost everyone has a bank account, carries debt, or is trying to save for the future.
            </p>
            <div class="stats-row">
              <div v-for="(s, i) in stats" :key="i" class="stat">
                <div class="stat-num">{{ statDisplay[i] }}</div>
                <div class="stat-label">{{ s.label }}</div>
              </div>
            </div>
            <div class="cta-area">
              <v-btn
                class="btn-gold"
                size="large"
                rounded="0"
                elevation="0"
                :href="amazonReady ? amazonUrl : undefined"
                :target="amazonReady ? '_blank' : undefined"
                :disabled="!amazonReady"
              >
                <v-icon start>mdi-amazon</v-icon>
                {{ amazonReady ? 'Available on Amazon' : 'Coming Soon to Amazon' }}
              </v-btn>
              <div class="coming-note">The Benevolism Series, Volume III — available now.</div>
            </div>
          </div>
        </v-container>
      </section>

      <!-- BLUEPRINT -->
      <section class="alt">
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Blueprint</div>
          <h2 class="heading">The Benevolent Financial Economy</h2>
          <p class="lede">
            A comprehensive vision of what the benevolent financial system looks like when all of its founding principles are operating together — five principles, working as one system.
          </p>
          <div class="layers-grid">
            <div
              v-for="(layer, i) in layers"
              :key="i"
              class="layer-card reveal"
              :style="{ transitionDelay: (i * 80) + 'ms' }"
            >
              <div class="layer-num">{{ String(i + 1).padStart(2, '0') }}</div>
              <h3 class="layer-name">{{ layer.name }}</h3>
              <p class="layer-desc">{{ layer.desc }}</p>
            </div>
          </div>

          <!-- INTERACTIVE PRICING MODEL DEMO -->
          <div class="pricing-demo reveal">
            <h3 class="pricing-heading">Cost-Based Pricing in Motion: How a Small-Dollar Loan Gets Priced</h3>
            <p class="pricing-sub">Affordable credit works because no single rate has to fit everyone. Toggle a model.</p>
            <div class="pricing-tabs">
              <button class="pricing-tab" :class="{ active: pricingMode === 'subscription' }" @click="pricingMode = 'subscription'">Flat-Fee Account</button>
              <button class="pricing-tab" :class="{ active: pricingMode === 'sliding' }" @click="pricingMode = 'sliding'">Progressive Rate</button>
              <button class="pricing-tab" :class="{ active: pricingMode === 'public' }" @click="pricingMode = 'public'">Community Investment</button>
            </div>

            <transition name="fade-swap" mode="out-in">
              <div v-if="pricingMode === 'subscription'" key="subscription" class="pricing-stage">
                <div class="pricing-visual">
                  <div class="price-bar-track">
                    <div class="price-bar-fill" :style="{ width: subscriptionBarPct + '%' }" />
                  </div>
                  <div class="price-readout">${{ subscriptionFee }}<span>/mo</span></div>
                </div>
                <p class="pricing-caption">One flat fee. Every account holder pays the same amount, every month, regardless of balance.</p>
              </div>

              <div v-else-if="pricingMode === 'sliding'" key="sliding" class="pricing-stage">
                <div class="pricing-visual">
                  <div class="price-bar-track">
                    <div class="price-bar-fill sliding" :style="{ width: slidingBarPct + '%' }" />
                  </div>
                  <div class="price-readout">{{ slidingCost }}<span>% APR</span></div>
                </div>
                <label class="slider-label">
                  Monthly Household Income: <strong>${{ slidingIncome.toLocaleString() }}</strong>
                  <input type="range" min="1500" max="12000" step="250" v-model.number="slidingIncome" class="income-slider" />
                </label>
                <p class="pricing-caption">Drag the slider. This is the inversion: the less a borrower has, the less they pay — because burying someone in interest is what makes them default, and a system that traps the poor in debt was never designed to help them thrive.</p>
              </div>

              <div v-else key="public" class="pricing-stage">
                <div class="pricing-visual pool-visual">
                  <div
                    v-for="(dot, n) in poolDots"
                    :key="n"
                    class="pool-dot"
                    :style="{ animationDelay: dot.delay + 's', '--dx': dot.dx + 'px', '--dy': dot.dy + 'px' }"
                  />
                  <div class="pool-center"><div class="pool-icon">$</div></div>
                </div>
                <p class="pricing-caption">Funded together through community capital — directed toward the local businesses and residents that mainstream finance overlooks.</p>
              </div>
            </transition>
          </div>

          <div class="transition-path">
            <h3 class="transition-heading">The Transition Path</h3>
            <p class="stage-text" style="text-align:center; margin-bottom:32px;">
              Building the benevolent financial economy from the existing extraction-based one is not a single event. It is a managed process of reform that changes incentives, prices, and institutions over time while maintaining the trust that savers and borrowers depend on.
            </p>
            <div class="timeline-line reveal-line" />
            <div class="transition-stages">
              <div
                v-for="(stage, i) in transitionStages"
                :key="i"
                class="reveal"
                :style="{ transitionDelay: (i * 120) + 'ms' }"
              >
                <div class="stage-label">{{ stage.label }}</div>
                <p class="stage-text">{{ stage.text }}</p>
              </div>
            </div>
            <p class="transition-close">"The transformation of finance begins, as all transformations begin, with individual decisions — and builds toward the institutions and policies that make those decisions the norm."</p>
          </div>
        </v-container>
      </section>

      <!-- TEN LAWS -->
      <section>
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Doctrine</div>
          <h2 class="heading">The Ten Laws of Benevolent Finance</h2>
          <p class="lede">
            The principles of Benevolism, applied to finance, yield binding standards — the line between a financial system that is genuinely benevolent and one that merely claims to be.
          </p>
          <div class="laws-grid">
            <div
              v-for="(law, i) in laws"
              :key="i"
              class="law-card reveal"
              :style="{ transitionDelay: (i * 40) + 'ms' }"
            >
              <div class="law-numeral">{{ law.numeral }}</div>
              <div>
                <div class="law-name">{{ law.name }}</div>
                <p class="law-desc">{{ law.desc }}</p>
              </div>
            </div>
          </div>
          <div class="pull-quote">
            <div class="mark">"</div>
            <blockquote>A financial system that takes from those who have the least is not a financial system. It is an extraction machine wearing a suit.</blockquote>
          </div>
        </v-container>
      </section>

      <!-- FINANCIAL PLEDGE -->
      <section class="alt">
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Pledge</div>
          <h2 class="heading">The Financial Pledge</h2>
          <p class="lede">
            The specific expression of the Benevolent Pledge, applied to the domain of money — offered in five versions: for financial institutions, for financial professionals, for investors and savers, for policymakers, and for society.
          </p>
          <div class="pledge-tabs">
            <button
              v-for="(pledge, i) in pledges"
              :key="i"
              class="pledge-tab"
              :class="{ active: activePledge === i }"
              @click="activePledge = i"
            >
              {{ pledge.role }}
            </button>
          </div>
          <div class="pledge-panel">
            <transition name="fade-swap" mode="out-in">
              <p :key="activePledge">{{ pledges[activePledge].text }}</p>
            </transition>
          </div>
        </v-container>
      </section>

      <!-- WHAT EACH OF US CAN DO -->
      <section>
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">A Call to Action</div>
          <h2 class="heading">What Each of Us Can Do</h2>
          <p class="lede">
            The transformation of finance does not require everyone to become a policymaker or a bank executive. It requires each of us to contribute what we can, from where we stand.
          </p>
          <div class="actions-grid">
            <div
              v-for="(action, i) in actions"
              :key="i"
              class="action-card reveal"
              :style="{ transitionDelay: (i * 80) + 'ms' }"
            >
              <div class="action-num">{{ String(i + 1).padStart(2, '0') }}</div>
              <div>
                <div class="action-title">{{ action.title }}</div>
                <p class="action-text">{{ action.text }}</p>
              </div>
            </div>
          </div>
        </v-container>
      </section>

      <!-- CLOSING -->
      <section class="closing">
        <v-container class="closing-inner reveal" style="max-width:780px;">
          <div class="mark">"</div>
          <blockquote>The most radical act in finance is honesty: charging what things actually cost, paying what they are actually worth, and designing every product to serve the person it is sold to.</blockquote>
          <div class="attr">— Enrico Michael Severe</div>
          <div class="closing-buttons">
            <v-btn class="btn-gold" size="large" rounded="0" elevation="0" @click="scrollToTop">
              Back to the Top
            </v-btn>
            <v-btn class="btn-ghost" size="large" rounded="0" elevation="0" @click="$emit('back')">
              ← Back to Benevolism
            </v-btn>
          </div>
        </v-container>
      </section>
    </main>

    <footer>
      <div class="footer-text">
        © {{ new Date().getFullYear() }} Enrico Michael Severe. Part of
        <a href="#" @click.prevent="$emit('back')">The Benevolism Series</a>. All rights reserved.
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'BenevolentBank',
  emits: ['back'],
  data: () => ({
    activePledge: 0,
    amazonReady: true,
    amazonUrl: 'https://www.amazon.com/BENEVOLENT-BANK-Benevolism-Democratize-Extraction/dp/B0H6M1Z43C/ref=sr_1_9?dib=eyJ2IjoiMSJ9.nAnangaUMLezmb8lJpTvVKIodBo_EwH5MSfQBHF7mvKtD9ke4FIebNJ4WYkdOyRpKVBuHMf6eRYQiXAhfJwETMKcd4BN9nsN6Ex9ckWGUT54tYB-tuua5aTr6z1TgZpkbroq6cHQrHm5skAvpMAYjeMZZLmz5qYZbosMkmfBPindSP9HThWGvWt5obOt_nMDZGQzwHkX933XggJR8-DFKIfNM8bvWULYY3aZw5-Qjoc.PMLmXTpK8fi2uzSiB0IOdFsRkK6EibXkqpUhJCRIBIE&dib_tag=se&qid=1784202163&refinements=p_27%3AEnrico+Michael+Severe&s=books&sr=1-9&text=Enrico+Michael+Severe',
    pricingMode: 'subscription',
    subscriptionFee: 9,
    slidingIncome: 4000,
    poolDots: [
      { dx: -90, dy: -40, delay: 0 },
      { dx: 90, dy: -40, delay: 0.5 },
      { dx: -90, dy: 40, delay: 1.0 },
      { dx: 90, dy: 40, delay: 1.5 },
      { dx: 0, dy: -65, delay: 2.0 },
      { dx: 0, dy: 65, delay: 2.5 },
    ],
    stats: [
      { label: 'Principles', value: 5 },
      { label: 'Laws', value: 10 },
      { label: 'Pledges', value: 5 },
      { label: 'Series Volume', value: 'III' },
    ],
    statDisplay: [0, 0, 0, 'III'],
    layers: [
      {
        name: 'Cost-Based Pricing',
        desc: 'Every financial product is priced at the genuine cost of delivering it plus a fair return — never at the maximum extractable from a captive borrower. For small-dollar and needs-based credit, this principle runs in reverse of conventional risk pricing: the less a borrower has, the less interest they pay, because a lower burden lowers the odds of default and lets low-income borrowers build wealth instead of surrendering it.',
      },
      {
        name: 'Universal Financial Access',
        desc: 'Basic financial services — a safe place to keep money, a reliable means of payment, and affordable credit for genuine needs — are available to every person who participates in the economy, at prices they can afford.',
      },
      {
        name: 'Radical Transparency',
        desc: 'Every financial product discloses its total cost in dollars — not just an interest rate or a monthly payment, but the full amount a customer will pay over the realistic life of the product.',
      },
      {
        name: 'Incentive Alignment',
        desc: 'Financial institutions earn more when their customers accumulate more wealth, not when their customers pay more fees. Compensation structures reward customer prosperity, not penalty revenue.',
      },
      {
        name: 'Anti-Exploitation',
        desc: 'Financial products may not be designed to exploit cognitive limitations, financial desperation, or captive position. The standard is not whether a product is legal, but whether it genuinely serves the person who buys it.',
      },
    ],
    transitionStages: [
      {
        label: 'Begins With',
        text: 'Individual decisions: moving accounts to institutions that charge honest fees, refinancing high-rate debt, choosing low-cost index funds, and demanding total cost disclosure before signing any financial document.',
      },
      {
        label: 'Proceeds Through',
        text: 'Founders and leaders who build financial businesses on benevolent principles, and investors who back them with patient capital oriented toward long-term, volume-based growth rather than short-term extraction.',
      },
      {
        label: 'Culminates In',
        text: 'Policymakers who enforce transparency, honest pricing, and anti-exploitation standards — resisting the lobbying power that has kept extraction as the default architecture of finance for generations.',
      },
    ],
    laws: [
      { numeral: 'I', name: 'The Law of Cost-Based Pricing', desc: 'Every financial product must be priced at the genuine cost of delivering it plus a fair return on capital and expertise. For the borrowers with the least, price must move in the opposite direction of conventional risk pricing — lower, not higher — because it is the burden of high interest, not the absence of it, that drives default. Any charge above this standard is extraction.' },
      { numeral: 'II', name: 'The Law of Universal Financial Access', desc: 'Basic financial services are not luxuries. They are infrastructure. Every person who participates in the modern economy must have access to them at prices they can afford.' },
      { numeral: 'III', name: 'The Law of Total Cost Transparency', desc: 'Every financial product must disclose its total cost in dollars over the realistic life of the product. The gap between advertised cost and experienced cost is where extraction lives.' },
      { numeral: 'IV', name: 'The Law of Anti-Exploitation', desc: 'Financial products may not be designed to exploit cognitive limitations, financial desperation, information asymmetry, or captive position, regardless of technical legality.' },
      { numeral: 'V', name: 'The Law of Incentive Alignment', desc: 'Financial institutions must be structured and compensated in ways that align their financial interests with the financial prosperity of the customers they serve.' },
      { numeral: 'VI', name: 'The Law of Debt Honesty', desc: 'Consumer debt must be designed and marketed honestly. Minimum payment structures must genuinely reduce principal over time, with no product marketed as free when it is not.' },
      { numeral: 'VII', name: 'The Law of Investment Integrity', desc: 'Investment fees must be disclosed in a way that makes their long-term dollar impact visible. The fiduciary standard is the minimum requirement for anyone who manages money for others.' },
      { numeral: 'VIII', name: 'The Law of Credit Fairness', desc: 'Credit assessment must measure genuine financial behavior, not the misfortune of illness, job loss, or poverty. The credit system must extend opportunity, not concentrate disadvantage.' },
      { numeral: 'IX', name: 'The Law of Retirement Security', desc: 'Every worker deserves access to retirement saving vehicles with low-cost, diversified investment defaults, transparent fee disclosure, and automatic enrollment.' },
      { numeral: 'X', name: 'The Law of Financial Governance Integrity', desc: 'Financial institutions must be governed in ways that place customer financial wellbeing at the center of every decision, with structure and enforcement that endure beyond good intentions.' },
    ],
    pledges: [
      { role: 'Institution', text: 'This institution exists to serve the financial needs of the people who trust us with their money. We commit to pricing our products at the cost of genuinely providing them plus a fair return — not at the maximum our market position allows us to extract. We will disclose our fees completely, honestly, and in terms that enable genuine comparison. We will design our products to help our customers achieve their financial goals, not to generate dependency or maximize our revenue from their mistakes. We will govern ourselves in ways that put customer financial health at the center of every decision.' },
      { role: 'Professional', text: 'I chose a career in finance to help people manage money wisely. I commit to practicing that profession in alignment with that purpose. I will recommend only what serves my clients\' genuine financial interests. I will disclose every fee, every conflict of interest, and every alternative I am aware of. I will be paid for the value I add to my clients\' financial lives, not for the products I can sell them. I will advocate within my industry for the structural reforms that align the financial profession\'s incentives with the financial wellbeing of the public it serves.' },
      { role: 'Investor / Saver', text: 'I commit to managing my financial life with the awareness that the financial system was not designed for my benefit and that conscious, informed participation is my most powerful tool. I will choose the lowest-cost financial products that meet my genuine needs. I will direct my financial relationships toward institutions that operate on benevolent terms. I will educate myself sufficiently to recognize extraction when I encounter it. And I will advocate — in my community, in my workplace, and through my political participation — for financial regulation that prioritizes the financial health of ordinary people.' },
      { role: 'Policymaker', text: 'I was entrusted with authority over financial regulation to protect the financial lives of the people I represent. I commit to exercising that authority in their interest, not in the interest of the industries I regulate. I will require genuine transparency, enforce honest pricing standards, and invest in the consumer financial protection that makes benevolent finance possible. I will resist the regulatory capture that has consistently aligned financial regulation with institutional profit rather than public benefit. I will measure the success of financial policy by household financial security, wealth accumulation accessibility, and the elimination of financial exclusion — not by the profitability of financial institutions.' },
      { role: 'Society', text: 'We commit to building a financial system that serves everyone who depends on it. We will design our institutions, our regulatory frameworks, and our cultural norms to reward benevolent finance and discipline extraction. We will invest in universal financial access as infrastructure, the way we invest in roads and schools. We will hold financial institutions accountable for the harm done by extraction-based products. And we will measure our civilization\'s financial health not by the size of our largest financial institutions but by the financial security of the most vulnerable households in our communities.' },
    ],
    actions: [
      { title: 'Savers & Borrowers', text: 'Move your accounts to a financial institution that charges honest fees, refinance high-rate debt when you can, choose low-cost index funds over high-fee active management, and demand total cost disclosure before signing any financial document.' },
      { title: 'Financial Professionals', text: 'Recommend only what serves your clients\' genuine financial interests, disclose every fee and conflict of interest, and advocate within your industry for the structural reforms benevolent finance requires.' },
      { title: 'Institutional Leaders', text: 'Build financial businesses on benevolent principles, price products at genuine cost, and seek patient capital oriented toward long-term, volume-based growth rather than short-term extraction maximization.' },
      { title: 'Policymakers & Investors', text: 'Resist the lobbying power of the financial industry, enforce consumer protection and anti-exploitation standards, and direct capital toward mission-driven lenders, credit unions, and CDFIs.' },
    ],
  }),
  computed: {
    subscriptionBarPct () {
      return Math.round((this.subscriptionFee / 40) * 100)
    },
    slidingCost () {
      const minApr = 3
      const maxApr = 15
      const minIncome = 1500
      const maxIncome = 12000
      const raw = minApr + ((this.slidingIncome - minIncome) / (maxIncome - minIncome)) * (maxApr - minApr)
      return Math.max(minApr, Math.min(maxApr, Math.round(raw * 2) / 2))
    },
    slidingBarPct () {
      return Math.round((this.slidingCost / 15) * 100)
    },
  },
  mounted () {
    window.scrollTo({ top: 0 })
    this.animateStats()
    this.$nextTick(() => this.setupReveal())
  },
  beforeUnmount () {
    if (this._revealObserver) this._revealObserver.disconnect()
  },
  methods: {
    scrollToTop () {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    animateStats () {
      const duration = 900
      this.stats.forEach((s, i) => {
        if (typeof s.value !== 'number') {
          this.$set(this.statDisplay, i, s.value)
          return
        }
        const target = s.value
        const start = performance.now()
        const tick = (now) => {
          const progress = Math.min(1, (now - start) / duration)
          this.$set(this.statDisplay, i, Math.round(target * progress))
          if (progress < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
      })
    },
    setupReveal () {
      const els = this.$el.querySelectorAll('.reveal, .reveal-line')
      if (!('IntersectionObserver' in window)) {
        els.forEach((el) => el.classList.add('is-visible'))
        return
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' })
      els.forEach((el) => observer.observe(el))
      this._revealObserver = observer
    },
  },
}
</script>

<style>
.bank-page {
  --vault: #1B1409;
  --vault-mid: #2A200E;
  --vault-light: #3D2E14;
  background: var(--vault);
  color: var(--ivory);
  font-family: var(--font-body);
}

/* ===== ENTRANCE + SCROLL REVEAL ===== */
@keyframes introRise {
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: translateY(0); }
}
.bank-page .intro-rise { animation: introRise 0.8s cubic-bezier(.16,1,.3,1) both; }
.bank-page .intro-rise-delayed { animation: introRise 0.8s cubic-bezier(.16,1,.3,1) 0.12s both; }

.bank-page .reveal {
  opacity: 0;
  transform: translateY(22px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}
.bank-page .reveal.is-visible { opacity: 1; transform: translateY(0); }

.bank-page .reveal-line {
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 0.9s ease;
}
.bank-page .reveal-line.is-visible { transform: scaleX(1); }

.bank-page .top-bar {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(27,20,9,0.92);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(201,168,76,0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 32px;
}
.bank-page .back-link {
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--gold-dim);
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
}
.bank-page .back-link:hover { color: var(--gold); }
.bank-page .top-brand {
  font-family: var(--font-ui);
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.18em;
  color: var(--ivory-dim);
  display: flex;
  align-items: center;
  gap: 8px;
}
.bank-page .top-brand .mark {
  width: 24px; height: 24px;
  background: var(--gold);
  color: var(--vault);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 13px;
}

.bank-page .eyebrow {
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3em;
  color: var(--gold);
  text-transform: uppercase;
  margin-bottom: 16px;
}
.bank-page .heading {
  font-family: var(--font-display);
  font-size: clamp(28px, 4vw, 44px);
  font-weight: 700;
  color: var(--ivory);
  line-height: 1.18;
  margin-bottom: 20px;
  text-align: center;
}
.bank-page .lede {
  font-family: var(--font-body);
  font-size: 17px;
  line-height: 1.75;
  color: var(--ivory-dim);
  font-weight: 300;
  text-align: center;
  max-width: 720px;
  margin: 0 auto 56px;
}
.bank-page section { padding: 96px 0; }
.bank-page section.alt { background: var(--vault-mid); border-top: 1px solid rgba(201,168,76,0.1); border-bottom: 1px solid rgba(201,168,76,0.1); }

.bank-page .hero { padding: 72px 0 96px; position: relative; overflow: hidden; }
.bank-page .hero::before {
  content: '';
  position: absolute;
  top: -200px; right: -200px;
  width: 700px; height: 700px;
  background: radial-gradient(circle, rgba(201,168,76,0.07) 0%, transparent 70%);
  pointer-events: none;
}
.bank-page .hero-inner {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 80px;
  align-items: start;
  position: relative;
  z-index: 1;
}
@media (max-width: 900px) {
  .bank-page .hero-inner { grid-template-columns: 1fr; }
  .bank-page .cover-col { display: flex; flex-direction: column; align-items: center; }
}
.bank-page .cover-col { position: relative; }
.bank-page .cover-card {
  width: 260px;
  min-height: 360px;
  background: linear-gradient(160deg, #3D2E14 0%, #1B1409 60%, #0A0703 100%);
  border: 1px solid rgba(201,168,76,0.4);
  box-shadow: 0 0 0 1px rgba(201,168,76,0.1), 8px 8px 32px rgba(0,0,0,0.6), inset 0 0 60px rgba(201,168,76,0.03);
  padding: 32px 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 9px;
  transform: rotate(-2deg);
  transition: transform 0.3s;
  position: relative;
  overflow: hidden;
}
.bank-page .cover-card::before {
  content: '';
  position: absolute;
  inset: 6px;
  border: 1px solid rgba(201,168,76,0.2);
  pointer-events: none;
}
.bank-page .cover-card::after {
  content: '';
  position: absolute;
  top: 0; left: -150%;
  width: 60%; height: 100%;
  background: linear-gradient(120deg, transparent, rgba(232,204,128,0.18), transparent);
  transform: skewX(-20deg);
  transition: left 0.7s ease;
  pointer-events: none;
}
.bank-page .cover-card:hover::after { left: 150%; }
.bank-page .cover-card:hover { transform: rotate(0deg) scale(1.02); }
.bank-page .cover-crown { font-size: 34px; margin-bottom: 2px; filter: sepia(1) hue-rotate(15deg) saturate(3) brightness(1.2); }
.bank-page .cover-title {
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 400;
  color: var(--gold-light);
  letter-spacing: 0.08em;
  line-height: 1.4;
}
.bank-page .cover-title strong { font-size: 21px; font-weight: 900; display: block; letter-spacing: 0.02em; }
.bank-page .cover-divider {
  width: 80%; height: 1px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  margin: 4px 0;
}
.bank-page .cover-sub { font-family: var(--font-body); font-size: 11px; color: var(--ivory-dim); line-height: 1.5; font-style: italic; }
.bank-page .cover-scales { font-size: 30px; filter: sepia(1) hue-rotate(15deg) saturate(3) brightness(1.1); margin: 4px 0; }
.bank-page .cover-series { font-family: var(--font-ui); font-size: 9px; letter-spacing: 0.15em; color: var(--ivory-dim); }
.bank-page .cover-author { font-family: var(--font-display); font-size: 12px; font-weight: 700; color: var(--gold-light); letter-spacing: 0.12em; }
.bank-page .cover-heir { font-family: var(--font-body); font-size: 9px; font-style: italic; color: var(--ivory-dim); }

.bank-page .badge {
  position: absolute;
  top: -12px; right: -12px;
  width: 76px; height: 76px;
  background: var(--gold);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--vault);
  animation: badgePop 0.6s cubic-bezier(.34,1.56,.64,1) 0.5s both;
}
@keyframes badgePop {
  from { opacity: 0; transform: rotate(15deg) scale(0.4); }
  to { opacity: 1; transform: rotate(15deg) scale(1); }
}
.bank-page .badge-top { font-family: var(--font-ui); font-size: 7px; font-weight: 700; letter-spacing: 0.08em; }
.bank-page .badge-main { font-family: var(--font-display); font-size: 11px; font-weight: 900; line-height: 1.1; }
.bank-page .badge-bottom { font-family: var(--font-ui); font-size: 8px; font-weight: 600; }

.bank-page .info-title {
  font-family: var(--font-display);
  font-size: clamp(30px, 3.4vw, 46px);
  font-weight: 700;
  color: var(--ivory);
  margin-bottom: 8px;
  line-height: 1.15;
}
.bank-page .info-sub {
  font-family: var(--font-display);
  font-size: 17px;
  font-style: italic;
  color: var(--gold-dim);
  margin-bottom: 22px;
  line-height: 1.5;
}
.bank-page .info-desc {
  font-family: var(--font-body);
  font-size: 16px;
  line-height: 1.75;
  color: var(--ivory-dim);
  margin-bottom: 32px;
  font-weight: 300;
  max-width: 620px;
}
.bank-page .stats-row {
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 36px;
  border: 1px solid rgba(201,168,76,0.15);
  width: fit-content;
}
.bank-page .stat { padding: 16px 26px; text-align: center; position: relative; }
.bank-page .stat:not(:last-child)::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 60px;
  background: rgba(201,168,76,0.15);
}
.bank-page .stat-num { font-family: var(--font-display); font-size: 30px; font-weight: 700; color: var(--gold); line-height: 1; }
.bank-page .stat-label { font-family: var(--font-ui); font-size: 9px; letter-spacing: 0.13em; color: var(--slate-light); text-transform: uppercase; margin-top: 4px; }

.bank-page .cta-area { display: flex; flex-direction: column; gap: 12px; align-items: flex-start; }
.bank-page .btn-gold { background: var(--gold) !important; color: var(--vault) !important; font-family: var(--font-ui) !important; font-size: 13px !important; font-weight: 700 !important; letter-spacing: 0.08em !important; padding: 0 32px !important; height: 52px !important; }
.bank-page .btn-gold:hover { background: var(--gold-light) !important; }
.bank-page .coming-note { font-family: var(--font-ui); font-size: 11px; color: var(--slate-light); letter-spacing: 0.02em; }

.bank-page .layers-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 2px; margin-bottom: 56px; }
.bank-page .layer-card { background: var(--vault); border: 1px solid rgba(201,168,76,0.08); padding: 32px 28px; transition: border-color 0.2s, background 0.2s; }
.bank-page .layer-card:hover { border-color: rgba(201,168,76,0.25); background: var(--vault-light); }
.bank-page .layer-num { font-family: var(--font-ui); font-size: 11px; font-weight: 700; color: var(--gold-dim); letter-spacing: 0.1em; margin-bottom: 12px; }
.bank-page .layer-name { font-family: var(--font-display); font-size: 17px; font-weight: 600; color: var(--ivory); margin-bottom: 10px; }
.bank-page .layer-desc { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

.bank-page .transition-path { max-width: 920px; margin: 0 auto; text-align: center; }
.bank-page .transition-heading { font-family: var(--font-display); font-size: 20px; font-weight: 600; color: var(--ivory); margin-bottom: 32px; }
.bank-page .timeline-line {
  height: 2px;
  max-width: 760px;
  margin: 0 auto 32px;
  background: linear-gradient(90deg, var(--gold-dim), var(--gold), var(--gold-dim));
}
.bank-page .transition-stages { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; text-align: left; margin-bottom: 32px; }
@media (max-width: 768px) { .bank-page .transition-stages { grid-template-columns: 1fr; } }
.bank-page .stage-label { font-family: var(--font-ui); font-size: 11px; font-weight: 700; letter-spacing: 0.1em; color: var(--gold); text-transform: uppercase; margin-bottom: 10px; }
.bank-page .stage-text { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }
.bank-page .transition-close { font-family: var(--font-display); font-style: italic; font-size: 15px; color: var(--ivory-dim); text-align: center; }

/* ===== INTERACTIVE PRICING DEMO ===== */
.bank-page .pricing-demo { max-width: 720px; margin: 0 auto 64px; text-align: center; }
.bank-page .pricing-heading { font-family: var(--font-display); font-size: 20px; font-weight: 600; color: var(--ivory); margin-bottom: 8px; }
.bank-page .pricing-sub { font-family: var(--font-body); font-size: 14px; color: var(--ivory-dim); font-weight: 300; margin-bottom: 28px; }
.bank-page .pricing-tabs { display: flex; gap: 8px; justify-content: center; margin-bottom: 32px; flex-wrap: wrap; }
.bank-page .pricing-tab {
  background: transparent;
  border: 1px solid rgba(201,168,76,0.2);
  color: var(--ivory-dim);
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  padding: 10px 20px;
  cursor: pointer;
  transition: all 0.2s;
}
.bank-page .pricing-tab:hover { border-color: var(--gold); color: var(--gold); }
.bank-page .pricing-tab.active { background: var(--gold); border-color: var(--gold); color: var(--vault); }

.bank-page .pricing-stage { min-height: 200px; }
.bank-page .pricing-visual { display: flex; flex-direction: column; align-items: center; gap: 16px; margin-bottom: 20px; }
.bank-page .price-bar-track {
  width: 100%;
  max-width: 360px;
  height: 14px;
  background: rgba(201,168,76,0.1);
  border: 1px solid rgba(201,168,76,0.2);
  overflow: hidden;
}
.bank-page .price-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--gold-dim), var(--gold));
  transition: width 0.35s ease;
}
.bank-page .price-readout { font-family: var(--font-display); font-size: 30px; font-weight: 700; color: var(--gold); }
.bank-page .price-readout span { font-family: var(--font-ui); font-size: 13px; font-weight: 500; color: var(--ivory-dim); margin-left: 4px; }

.bank-page .slider-label {
  display: block;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--ivory-dim);
  letter-spacing: 0.04em;
  margin-bottom: 16px;
}
.bank-page .slider-label strong { color: var(--gold); font-weight: 700; }
.bank-page .income-slider {
  display: block;
  width: 100%;
  max-width: 360px;
  margin: 10px auto 0;
  appearance: none;
  -webkit-appearance: none;
  height: 4px;
  background: rgba(201,168,76,0.2);
  border-radius: 2px;
  outline: none;
}
.bank-page .income-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px; height: 18px;
  border-radius: 50%;
  background: var(--gold);
  border: 2px solid var(--vault);
  cursor: pointer;
  transition: transform 0.15s ease;
}
.bank-page .income-slider::-webkit-slider-thumb:hover { transform: scale(1.15); }
.bank-page .income-slider::-moz-range-thumb {
  width: 18px; height: 18px;
  border-radius: 50%;
  background: var(--gold);
  border: 2px solid var(--vault);
  cursor: pointer;
}
.bank-page .pricing-caption {
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.6;
  color: var(--ivory-dim);
  font-weight: 300;
  max-width: 460px;
  margin: 0 auto;
}

.bank-page .pool-visual { position: relative; width: 200px; height: 140px; margin: 0 auto; }
.bank-page .pool-center {
  position: absolute;
  left: 50%; top: 50%;
  transform: translate(-50%, -50%);
  width: 56px; height: 56px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--gold) 0%, var(--gold-dim) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 24px rgba(201,168,76,0.4);
}
.bank-page .pool-icon { font-size: 22px; color: var(--vault); }
.bank-page .pool-dot {
  position: absolute;
  width: 8px; height: 8px;
  border-radius: 50%;
  background: var(--gold-light);
  top: 50%; left: 50%;
  margin: -4px;
  opacity: 0;
  animation: flowIn 3s ease-in infinite;
}
@keyframes flowIn {
  0% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(1); }
  15% { opacity: 1; }
  85% { opacity: 1; transform: translate(calc(var(--dx) * 0.08), calc(var(--dy) * 0.08)) scale(0.5); }
  100% { opacity: 0; transform: translate(0, 0) scale(0.3); }
}

/* fade-swap transition, used for pledge text and pricing stage swaps */
/* Vue 3 naming */
.bank-page .fade-swap-enter-active,
.bank-page .fade-swap-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.bank-page .fade-swap-enter-from { opacity: 0; transform: translateY(6px); }
.bank-page .fade-swap-leave-to { opacity: 0; transform: translateY(-6px); }
/* Vue 2 naming (same active/to classes, "from" state is unsuffixed) */
.bank-page .fade-swap-enter { opacity: 0; transform: translateY(6px); }
.bank-page .fade-swap-leave { opacity: 0; transform: translateY(-6px); }

.bank-page .laws-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 2px; margin-bottom: 48px; }
@media (max-width: 768px) { .bank-page .laws-grid { grid-template-columns: 1fr; } }
.bank-page .law-card { display: flex; gap: 22px; padding: 32px 36px; background: var(--vault); border: 1px solid rgba(201,168,76,0.06); transition: border-color 0.2s, background 0.2s; }
.bank-page .law-card:hover { border-color: rgba(201,168,76,0.2); background: var(--vault-light); }
.bank-page .law-numeral { font-family: var(--font-display); font-size: 13px; font-style: italic; color: var(--gold-dim); flex-shrink: 0; padding-top: 2px; width: 22px; }
.bank-page .law-name { font-family: var(--font-display); font-size: 16px; font-weight: 600; color: var(--ivory); margin-bottom: 9px; }
.bank-page .law-desc { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

.bank-page .pull-quote { max-width: 640px; margin: 0 auto; text-align: center; }
.bank-page .pull-quote .mark { font-family: var(--font-display); font-size: 48px; color: var(--gold); line-height: 1; }
.bank-page .pull-quote blockquote { font-family: var(--font-display); font-style: italic; font-size: 19px; line-height: 1.55; color: var(--ivory); margin: 4px 0 0; }

.bank-page .pledge-tabs { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-bottom: 40px; }
.bank-page .pledge-tab { background: transparent; border: 1px solid rgba(201,168,76,0.2); color: var(--ivory-dim); font-family: var(--font-ui); font-size: 12px; font-weight: 600; letter-spacing: 0.05em; padding: 11px 22px; cursor: pointer; transition: all 0.2s; }
.bank-page .pledge-tab:hover { border-color: var(--gold); color: var(--gold); }
.bank-page .pledge-tab.active { background: var(--gold); border-color: var(--gold); color: var(--vault); }
.bank-page .pledge-panel { max-width: 760px; margin: 0 auto; background: var(--vault-mid); border: 1px solid rgba(201,168,76,0.12); padding: 44px 48px; }
.bank-page .pledge-panel p { font-family: var(--font-body); font-size: 15px; line-height: 1.8; color: var(--ivory-dim); font-weight: 300; }

.bank-page .actions-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 2px; }
.bank-page .action-card { display: flex; gap: 20px; padding: 34px; background: var(--vault-mid); border: 1px solid rgba(201,168,76,0.08); transition: border-color 0.2s, background 0.2s; }
.bank-page .action-card:hover { border-color: rgba(201,168,76,0.25); background: var(--vault-light); }
.bank-page .action-num { font-family: var(--font-ui); font-size: 11px; font-weight: 700; color: var(--gold-dim); letter-spacing: 0.1em; padding-top: 4px; width: 26px; flex-shrink: 0; }
.bank-page .action-title { font-family: var(--font-display); font-size: 17px; font-weight: 600; color: var(--ivory); margin-bottom: 10px; }
.bank-page .action-text { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

.bank-page .closing { text-align: center; padding: 110px 0 96px; position: relative; overflow: hidden; }
.bank-page .closing::before {
  content: '';
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 600px; height: 600px;
  background: radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%);
  pointer-events: none;
}
.bank-page .closing-inner { position: relative; z-index: 1; }
.bank-page .closing .mark { font-family: var(--font-display); font-size: 56px; color: var(--gold); line-height: 1; }
.bank-page .closing blockquote { font-family: var(--font-display); font-style: italic; font-size: clamp(22px, 3vw, 30px); line-height: 1.5; color: var(--ivory); margin: 8px 0 20px; }
.bank-page .closing .attr { font-family: var(--font-ui); font-size: 12px; letter-spacing: 0.15em; color: var(--gold-dim); text-transform: uppercase; margin-bottom: 48px; }
.bank-page .closing-buttons { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }
.bank-page .btn-ghost { background: transparent !important; border: 1px solid rgba(245,240,232,0.25) !important; color: var(--ivory-dim) !important; font-family: var(--font-ui) !important; font-size: 13px !important; font-weight: 500 !important; letter-spacing: 0.06em !important; padding: 0 32px !important; height: 52px !important; }
.bank-page .btn-ghost:hover { border-color: var(--ivory-dim) !important; color: var(--ivory) !important; }

.bank-page footer { padding: 40px 0; background: var(--vault-mid); border-top: 1px solid rgba(201,168,76,0.12); text-align: center; }
.bank-page .footer-text { font-family: var(--font-ui); font-size: 11px; color: rgba(200,192,176,0.5); letter-spacing: 0.05em; }
.bank-page .footer-text a { color: var(--gold-dim); text-decoration: none; cursor: pointer; }
.bank-page .footer-text a:hover { color: var(--gold); }

/* ===== REDUCED MOTION ===== */
@media (prefers-reduced-motion: reduce) {
  .bank-page * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  .bank-page .reveal,
  .bank-page .reveal-line {
    opacity: 1 !important;
    transform: none !important;
  }
}
</style>